/**
 * Shared ParadigmSheet bottom-sheet overlay (read + admin edit).
 * Used by both WordDetail and QuranVerbLinkPanel.
 *
 * Props:
 *   senseId  — sense PK to load paradigm for
 *   wordText — Arabic word text shown in header
 *   isAdmin  — bool; shows edit controls when true
 *   onClose  — called when user dismisses the sheet
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  fetchSenseParadigm,
  createConjugation,
  deleteConjugation,
  reorderConjugations,
} from "../api";

export function tagsToLabel(tags) {
  const s = new Set(tags);
  const parts = [];
  if (s.has("first-person")) parts.push("1st");
  else if (s.has("second-person")) parts.push("2nd");
  else if (s.has("third-person")) parts.push("3rd");
  if (s.has("masculine")) parts.push("masc.");
  else if (s.has("feminine")) parts.push("fem.");
  if (s.has("singular")) parts.push("sg.");
  else if (s.has("dual")) parts.push("dual");
  else if (s.has("plural")) parts.push("pl.");
  if (s.has("imperative")) parts.push("imperative");
  else if (s.has("non-past") || s.has("imperfective")) parts.push("present");
  else if (s.has("past") || s.has("perfective")) parts.push("past");
  if (s.has("passive")) parts.push("passive");
  else if (s.has("active") && (s.has("past") || s.has("non-past") || s.has("perfective") || s.has("imperfective")))
    parts.push("active");
  if (s.has("subjunctive")) parts.push("subj.");
  else if (s.has("jussive")) parts.push("juss.");
  return parts.join(" ") || "—";
}

/**
 * Group flat conjugation list into sections suitable for the paradigm table.
 * Returns [{ voice, tense, label, forms }] sorted in canonical order.
 */
export function groupConjugations(conjugations) {
  const buckets = {};
  for (const c of conjugations) {
    const tags = new Set(c.tags);
    const voice = tags.has("passive") ? "passive" : "active";
    let tense;
    if (tags.has("imperative")) tense = "imperative";
    else if (tags.has("non-past") || tags.has("imperfective")) tense = "present";
    else if (tags.has("past") || tags.has("perfective")) tense = "past";
    else if (tags.has("participle")) tense = "participle";
    else if (tags.has("noun-from-verb")) tense = "masdar";
    else tense = "other";
    const key = `${voice}_${tense}`;
    if (!buckets[key]) buckets[key] = { voice, tense, forms: [] };
    buckets[key].forms.push(c);
  }
  const ORDER = [
    "active_past", "active_present", "active_imperative",
    "passive_past", "passive_present",
    "active_participle", "passive_participle",
    "active_masdar", "active_other", "passive_other",
  ];
  const LABELS = {
    active_past: "Past (active)", active_present: "Present (active)", active_imperative: "Imperative",
    passive_past: "Past (passive)", passive_present: "Present (passive)",
    active_participle: "Active participle", passive_participle: "Passive participle",
    active_masdar: "Masdar / verbal noun",
    active_other: "Other (active)", passive_other: "Other (passive)",
  };
  return ORDER.filter((k) => buckets[k]).map((k) => ({
    ...buckets[k],
    label: LABELS[k] || k,
  }));
}

// ── Tag builder helpers ────────────────────────────────────────────────────

const TAG_OPTIONS = {
  person:  [["", "—"], ["first-person", "1st"], ["second-person", "2nd"], ["third-person", "3rd"]],
  gender:  [["", "—"], ["masculine", "Masc."], ["feminine", "Fem."]],
  number:  [["", "—"], ["singular", "Sg."], ["dual", "Dual"], ["plural", "Pl."]],
  tense:   [["", "—"], ["past", "Past"], ["non-past", "Present"], ["imperative", "Imperative"], ["subjunctive", "Subj."], ["jussive", "Juss."]],
  voice:   [["", "—"], ["active", "Active"], ["passive", "Passive"]],
  extra:   [["", "—"], ["participle", "Participle"], ["noun-from-verb", "Masdar"]],
};

function buildTags({ person, gender, number, tense, voice, extra }) {
  return [person, gender, number, tense, voice, extra].filter(Boolean);
}

function TagSelect({ label, options, value, onChange }) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs rounded border border-slate-300 px-1 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function AddConjugationForm({ senseId, onAdded, onCancel }) {
  const [text, setText] = useState("");
  const [person, setPerson] = useState("");
  const [gender, setGender] = useState("");
  const [number, setNumber] = useState("");
  const [tense, setTense] = useState("");
  const [voice, setVoice] = useState("");
  const [extra, setExtra] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      const tags = buildTags({ person, gender, number, tense, voice, extra });
      const conj = await createConjugation(senseId, { text: trimmed, tags });
      onAdded(conj);
      setText(""); setPerson(""); setGender(""); setNumber(""); setTense(""); setVoice(""); setExtra("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-2 rounded-xl border border-dashed border-emerald-300 bg-emerald-50 px-3 py-3 space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Add conjugation</p>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Arabic form (with diacritics)"
        dir="rtl"
        lang="ar"
        className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-sm font-arabic focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />
      <div className="grid grid-cols-3 gap-1.5">
        <TagSelect label="Person" options={TAG_OPTIONS.person} value={person} onChange={setPerson} />
        <TagSelect label="Gender" options={TAG_OPTIONS.gender} value={gender} onChange={setGender} />
        <TagSelect label="Number" options={TAG_OPTIONS.number} value={number} onChange={setNumber} />
        <TagSelect label="Tense/Mood" options={TAG_OPTIONS.tense} value={tense} onChange={setTense} />
        <TagSelect label="Voice" options={TAG_OPTIONS.voice} value={voice} onChange={setVoice} />
        <TagSelect label="Extra" options={TAG_OPTIONS.extra} value={extra} onChange={setExtra} />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} disabled={saving} className="px-3 py-1 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-50">Cancel</button>
        <button type="submit" disabled={!text.trim() || saving} className="px-3 py-1 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{saving ? "Saving…" : "Add"}</button>
      </div>
    </form>
  );
}

// ── ParadigmSheet ──────────────────────────────────────────────────────────

export default function ParadigmSheet({ senseId, wordText, isAdmin, onClose }) {
  const [data, setData] = useState(null);
  const [conjugations, setConjugations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [addingToGroup, setAddingToGroup] = useState(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const reload = useCallback(() => {
    if (!senseId) return;
    setLoading(true);
    setError(null);
    fetchSenseParadigm(senseId)
      .then((d) => { setData(d); setConjugations(d.conjugations); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [senseId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Cross-browser body scroll lock.
  // - position:fixed + overflow:hidden covers iOS Safari (overflow:hidden alone doesn't work there)
  // - overflow:hidden alone is enough for Android Chrome / Firefox / Samsung Internet,
  //   so this combination handles all mobile browsers.
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  const groups = groupConjugations(conjugations);

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this conjugation?")) return;
    try {
      await deleteConjugation(id);
      setConjugations((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDragStart = (e, id) => {
    dragItem.current = id;
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    dragOverItem.current = id;
  };
  const handleDrop = async (e, groupForms) => {
    e.preventDefault();
    const fromId = dragItem.current;
    const toId = dragOverItem.current;
    if (!fromId || !toId || fromId === toId) return;
    const ids = groupForms.map((f) => f.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = [...ids];
    reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, fromId);
    const groupIdSet = new Set(ids);
    const otherConjs = conjugations.filter((c) => !groupIdSet.has(c.id));
    const groupConjs = reordered.map((id) => conjugations.find((c) => c.id === id));
    const newAll = [...otherConjs, ...groupConjs];
    setConjugations(newAll);
    dragItem.current = null;
    dragOverItem.current = null;
    try {
      await reorderConjugations(senseId, newAll.map((c) => c.id));
    } catch (err) {
      alert("Reorder failed: " + err.message);
      reload();
    }
  };

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} aria-hidden="true" />
      <div className="dark-scope fixed inset-x-0 bottom-0 z-50 max-h-[85vh] flex flex-col rounded-t-2xl surface shadow-2xl overflow-hidden">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 shrink-0">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Conjugation table</p>
            <p className="font-arabic text-xl font-bold text-slate-800" dir="rtl">{wordText}</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && !loading && (
              <button
                onClick={() => { setEditMode((m) => !m); setAddingToGroup(null); }}
                className={`px-2.5 py-1 text-xs rounded-lg border font-medium transition-colors ${editMode ? "bg-amber-100 border-amber-400 text-amber-800" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
              >
                {editMode ? "Done editing" : "Edit table"}
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {data && (
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 shrink-0">
            <p className="text-sm text-slate-600">{data.definition}</p>
          </div>
        )}

        {editMode && (
          <div className="px-4 py-1.5 bg-amber-50 border-b border-amber-100 shrink-0">
            <p className="text-xs text-amber-700">Drag cards to reorder within a group · ✕ to remove · + to add</p>
          </div>
        )}

        {/* Body — overscroll-contain stops scroll chaining on all browsers */}
        <div className="overflow-y-auto overscroll-contain flex-1 px-4 py-3 space-y-5">
          {loading && <p className="text-sm text-slate-400 text-center py-8">Loading conjugations…</p>}
          {error && <p className="text-sm text-red-600 text-center py-8">{error}</p>}
          {!loading && !error && groups.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">
              No conjugations stored yet.{isAdmin && " Use Re-fetch or add manually below."}
            </p>
          )}

          {groups.map((group) => {
            const groupKey = `${group.voice}_${group.tense}`;
            return (
              <section key={groupKey}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{group.label}</h3>
                  {editMode && (
                    <button
                      type="button"
                      onClick={() => setAddingToGroup(addingToGroup === groupKey ? null : groupKey)}
                      className="text-xs px-2 py-0.5 rounded border border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                    >
                      + Add
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {group.forms.map((form) => (
                    <div
                      key={form.id ?? form.normalized_text}
                      draggable={editMode}
                      onDragStart={editMode ? (e) => handleDragStart(e, form.id) : undefined}
                      onDragOver={editMode ? (e) => handleDragOver(e, form.id) : undefined}
                      onDrop={editMode ? (e) => handleDrop(e, group.forms) : undefined}
                      className={`relative flex flex-col items-center rounded-xl border border-slate-200 bg-slate-50 px-2 py-2 gap-0.5 ${editMode ? "cursor-grab active:cursor-grabbing hover:border-amber-300 hover:bg-amber-50" : ""}`}
                    >
                      {editMode && (
                        <button
                          type="button"
                          onClick={() => handleDelete(form.id)}
                          className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 text-[10px] leading-none"
                          title="Remove"
                        >✕</button>
                      )}
                      {editMode && (
                        <span className="absolute top-1 left-1 text-slate-300 text-[10px] select-none">⠿</span>
                      )}
                      <span className="font-arabic text-base font-semibold text-slate-800" dir="rtl">{form.text}</span>
                      <span className="text-[10px] text-slate-400 text-center leading-tight">{tagsToLabel(form.tags)}</span>
                    </div>
                  ))}
                </div>
                {editMode && addingToGroup === groupKey && (
                  <AddConjugationForm
                    senseId={senseId}
                    onAdded={(conj) => { setConjugations((prev) => [...prev, conj]); setAddingToGroup(null); }}
                    onCancel={() => setAddingToGroup(null)}
                  />
                )}
              </section>
            );
          })}

          {editMode && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-300">New group</h3>
                <button
                  type="button"
                  onClick={() => setAddingToGroup(addingToGroup === "__new__" ? null : "__new__")}
                  className="text-xs px-2 py-0.5 rounded border border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                >
                  + Add
                </button>
              </div>
              {addingToGroup === "__new__" && (
                <AddConjugationForm
                  senseId={senseId}
                  onAdded={(conj) => { setConjugations((prev) => [...prev, conj]); setAddingToGroup(null); }}
                  onCancel={() => setAddingToGroup(null)}
                />
              )}
            </section>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
